// ante-react-kit/src/components/AntQuickPopup/helpers/QPActions.js
import React, { useMemo, useState, useCallback } from 'react';
import { Button } from 'devextreme-react/button';
import { useQuickPopupContext } from '../QuickPopupContext';
import AntLocalization from '../../../utils/AntLocalization';

// Default per-action behavior
const DEFAULTS = {
    actionRelease:    { closeOnSuccess: true, notifyParent: true, confirmRequired: false },
    actionClose:      { closeOnSuccess: true, notifyParent: true, confirmRequired: false },
    actionPost:       { closeOnSuccess: true, notifyParent: true, confirmRequired: false },
    actionApprove:    { closeOnSuccess: true, notifyParent: true, confirmRequired: false },
    actionReject:     { closeOnSuccess: true, notifyParent: true, confirmRequired: true,
                       confirmMessage: { vi: 'Từ chối phiếu này?', en: 'Reject this voucher?', fr: 'Rejeter cette pièce ?', es: '¿Rechazar este documento?', id: 'Tolak voucher ini?', ja: 'この伝票を却下しますか?', zh: '拒绝此单据?', ko: '이 전표를 반려할까요?', hi: 'यह वाउचर अस्वीकार करें?', ar: 'رفض هذا السند؟' } },
    actionDelete:     { closeOnSuccess: true, notifyParent: true, confirmRequired: true,
                       confirmMessage: { vi: 'Xoá phiếu này?', en: 'Delete this voucher?', fr: 'Supprimer cette pièce ?', es: '¿Eliminar este documento?', id: 'Hapus voucher ini?', ja: 'この伝票を削除しますか?', zh: '删除此单据?', ko: '이 전표를 삭제할까요?', hi: 'यह वाउचर हटाएँ?', ar: 'حذف هذا السند؟' } },
    actionCancel:     { closeOnSuccess: true, notifyParent: true, confirmRequired: true,
                       confirmMessage: { vi: 'Huỷ phiếu này?', en: 'Cancel this voucher?', fr: 'Annuler cette pièce ?', es: '¿Anular este documento?', id: 'Batalkan voucher ini?', ja: 'この伝票を取消しますか?', zh: '取消此单据?', ko: '이 전표를 취소할까요?', hi: 'यह वाउचर रद्द करें?', ar: 'إلغاء هذا السند؟' } },
    actionRecalculate:{ closeOnSuccess: false, notifyParent: false, confirmRequired: false },
    actionRefresh:    { closeOnSuccess: false, notifyParent: false, confirmRequired: false }
};

const DESTRUCTIVE = ['actionDelete', 'actionCancel', 'actionReject'];

export default function QPActions(props) {
    const { filter, hide, overrides = {}, layout = 'horizontal' } = props;
    const ctx = useQuickPopupContext();
    const [running, setRunning] = useState(null);

    const visibleActions = useMemo(() => {
        let arr = ctx.actions || [];
        if (filter) arr = arr.filter((a) => filter.includes(a.name));
        if (hide) arr = arr.filter((a) => !hide.includes(a.name));
        return arr.filter((a) => a.visible !== false);
    }, [ctx.actions, filter, hide]);

    const handleClick = useCallback(async (action) => {
        const def = { ...(DEFAULTS[action.name] || {}), ...(overrides[action.name] || {}) };

        if (def.confirmRequired) {
            const msg = AntLocalization.translateText(def.confirmMessage || { vi: 'Xác nhận?', en: 'Confirm?', fr: 'Confirmer ?', es: '¿Confirmar?', id: 'Konfirmasi?', ja: '確認?', zh: '确认?', ko: '확인할까요?', hi: 'पुष्टि करें?', ar: 'تأكيد؟' });
            if (!window.confirm(msg)) return;
        }

        if (def.onBeforeRun) await def.onBeforeRun(ctx);

        setRunning(action.name);
        try {
            const res = await ctx.runAction(action.name);
            if (def.onAfterRun) await def.onAfterRun(res, ctx);
            if (res.success) {
                if (def.notifyParent && ctx.onActionDone) ctx.onActionDone(action.name, res);
                if (def.closeOnSuccess && ctx.onCloseRequest) ctx.onCloseRequest();
            }
        } finally {
            setRunning(null);
        }
    }, [ctx, overrides]);

    if (!visibleActions.length) return null;

    return (
        <div className="qp-actions" style={{
            display: 'flex',
            flexDirection: layout === 'dropdown-menu' ? 'column' : 'row',
            gap: 8, flexWrap: 'wrap'
        }}>
            {visibleActions.map((action) => {
                const def = { ...(DEFAULTS[action.name] || {}), ...(overrides[action.name] || {}) };
                const text = def.text
                    ? AntLocalization.translateText(def.text)
                    : (action.text ? AntLocalization.translateText(action.text) : action.name);
                const isDestructive = DESTRUCTIVE.includes(action.name);
                return (
                    <Button
                        key={action.name}
                        text={text}
                        icon={def.icon || action.icon}
                        type={isDestructive ? 'danger' : (action.name === 'actionRelease' ? 'default' : 'normal')}
                        disabled={action.disabled === true || running === action.name}
                        onClick={() => handleClick(action)}
                    />
                );
            })}
        </div>
    );
}
